import { useState } from 'react';
import { useGT } from 'gt-react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useProductAssignees } from '@/api/rpc/hooks/core/useProductAssignees';
import { useUsersByIds } from '@/api/core/users/useUsersByIds';
import {
  assigneeTriggerLabel,
  findAssigneeUser,
  shouldLookupAssigneeName,
} from './assigneePickerDisplay';

/** Sentinel row id for the "Unassigned" option (maps back to null). */
const UNASSIGNED = '__unassigned__';

interface AssigneePickerProps {
  /** Currently assigned user id, or null when unassigned. */
  value: string | null | undefined;
  /** Fired with the new user id, or null to clear the assignment. */
  onChange: (userId: string | null) => void;
  disabled?: boolean;
  triggerId?: string;
  unassignedLabel?: string;
  placeholder?: string;
}

/**
 * Searchable dropdown (Popover + Command) for assigning a change set to a user
 * with full Product permissions. Filtering is client-side over the cached
 * assignee list, which is small.
 */
export function AssigneePicker({
  value,
  onChange,
  disabled,
  triggerId,
  unassignedLabel,
  placeholder,
}: AssigneePickerProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const { data: users, isLoading } = useProductAssignees();
  const lookupName = shouldLookupAssigneeName(value, users);
  const { data: lookedUpUsers } = useUsersByIds(
    lookupName ? [value] : [],
    lookupName,
  );
  const selected =
    findAssigneeUser(value, users) ?? findAssigneeUser(value, lookedUpUsers);
  const resolvedUnassignedLabel =
    unassignedLabel ??
    gt('Unassigned', { context: 'option label, no user assigned' });
  const triggerLabel = assigneeTriggerLabel({
    selectedName: selected?.name,
    value,
    placeholder,
    unassignedLabel: resolvedUnassignedLabel,
    unresolvedLabel: gt('Loading…', {
      context: 'loading state, assignee picker',
    }),
  });

  function handleSelect(userId: string | null) {
    onChange(userId);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={triggerId}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className="w-full justify-between font-normal"
        >
          <span
            className={cn('truncate', !selected && 'text-muted-foreground')}
          >
            {triggerLabel}
          </span>
          <CaretDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--anchor-width) p-0" align="start">
        <Command>
          <CommandInput
            placeholder={gt('Search users…', {
              context: 'placeholder, assignee search box',
            })}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? gt('Loading…', { context: 'loading state, assignee picker' })
                : gt('No users found.', {
                    context: 'empty state, assignee picker',
                  })}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={`${resolvedUnassignedLabel} ${UNASSIGNED}`}
                onSelect={() => handleSelect(null)}
              >
                <CheckIcon
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === null ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {resolvedUnassignedLabel}
              </CommandItem>
              {(users ?? []).map((user) => (
                <CommandItem
                  key={user.id}
                  // cmdk filters on this value; include email so search matches it
                  value={`${user.name} ${user.email ?? ''}`}
                  onSelect={() => handleSelect(user.id)}
                >
                  <CheckIcon
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      value === user.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="min-w-0 truncate">
                    {user.name}
                    {user.email ? (
                      <span className="text-muted-foreground">
                        {' '}
                        · {user.email}
                      </span>
                    ) : null}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
